// Client-side PDF keepsake via browser print. Journal content never leaves the
// authenticated browser session for export.
import { formatDateGB, meLabel } from "@/lib/me";

function esc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

const STYLE = `
  @page { margin: 22mm 18mm; }
  * { box-sizing: border-box; }
  body { background:#fbf7ef; color:#2b2620; font-family: Georgia, 'Times New Roman', serif; margin:0; padding:32px; }
  .cover { text-align:center; padding:48px 0 40px; border-bottom:1px solid #d8cdb8; margin-bottom:36px; }
  .kicker { font-family: Arial, sans-serif; text-transform:uppercase; letter-spacing:4px; font-size:11px; color:#a8895c; }
  h1 { font-size:40px; margin:14px 0 8px; color:#1c1712; font-weight:normal; }
  .sub { color:#6b6154; font-size:15px; margin:4px 0; }
  .intention { font-style:italic; color:#6b6154; margin-top:14px; font-size:16px; }
  h2 { font-family: Arial, sans-serif; text-transform:uppercase; letter-spacing:2px; font-size:13px; color:#a8895c; margin:30px 0 14px; }
  .state { margin-top:34px; }
  .state-head { font-size:26px; color:#1c1712; border-bottom:1px solid #e3d8c2; padding-bottom:6px; }
  .q { font-family: Arial, sans-serif; font-weight:bold; font-size:13px; color:#2b2620; margin:16px 0 6px; }
  .a { border-left:2px solid #e3d8c2; padding-left:14px; white-space:pre-wrap; font-size:15px; line-height:1.6; color:#2b2620; }
  .empty { color:#6b6154; font-style:italic; }
`;

function answeredSectionsHTML(state, answers) {
  let out = "";
  let any = false;
  for (const section of state.sections) {
    const items = section.prompts.filter((p) => (answers[p.id] || "").trim());
    if (!items.length) continue;
    any = true;
    out += `<h2>${esc(section.title)}</h2>`;
    for (const p of items) {
      out += `<div class="q">${esc(p.label)}</div><div class="a">${esc(answers[p.id])}</div>`;
    }
  }
  if (!any) out = `<p class="empty">No reflections recorded for this state yet.</p>`;
  return out;
}

function coverHTML(title, sub, intention) {
  return `<div class="cover"><div class="kicker">The Emergence Journal</div>
    <h1>${esc(title)}</h1><div class="sub">${esc(sub)}</div>
    ${intention ? `<div class="intention">\u201c${esc(intention)}\u201d</div>` : ""}</div>`;
}

function printDoc(html) {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><style>${STYLE}</style></head><body>${html}</body></html>`);
  doc.close();
  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 250);
  };
}

export function exportMonth(state, entry, user) {
  const answers = (entry && entry.answers) || {};
  const stateLabel = state.id === 0 ? "Baseline" : `Month ${state.id} of 11`;
  const heading = user?.cover_title || `${state.me}: ${state.theme}`;
  const sub = `${state.me}: ${state.theme} \u00b7 ${formatDateGB()}`;
  const html = coverHTML(heading, sub, user?.cover_intention) +
    `<div class="state"><div class="state-head">${esc(state.me)}: ${esc(state.theme)} \u00b7 ${esc(stateLabel)}</div>` +
    answeredSectionsHTML(state, answers) + `</div>`;
  printDoc(html);
}

export function exportYear(states, entriesByMonth, user) {
  const heading = user?.cover_title || `${meLabel(0)} : ${meLabel(11)}`;
  const sub = `${user?.name || "Your"}'s twelve-month emergence \u00b7 ${formatDateGB()}`;
  let body = coverHTML(heading, sub, user?.cover_intention);
  for (const state of states) {
    const answers = (entriesByMonth[state.id] && entriesByMonth[state.id].answers) || {};
    const stateLabel = state.id === 0 ? "Baseline" : `Month ${state.id} of 11`;
    body += `<div class="state"><div class="state-head">${esc(state.me)}: ${esc(state.theme)} \u00b7 ${esc(stateLabel)}</div>` +
      answeredSectionsHTML(state, answers) + `</div>`;
  }
  printDoc(body);
}
